/**
 * 按 msg_type 分发渲染。见设计 §7.3。
 *
 * `content` 是 Feishu `im/v1/messages/:id` 原 payload——`body.content` 通常是
 * JSON 字符串，少数接口会直接返回 dict，两种都要兼容。
 */

import type { Item } from "@/api/items";

function parseBodyContent(raw: unknown): Record<string, unknown> {
  if (raw && typeof raw === "object") return raw as Record<string, unknown>;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return {};
}

function extractBody(content: Record<string, unknown>): Record<string, unknown> {
  // 标准 im/v1/messages 格式：{ body: { content: "..." | {...} } }
  const body = content.body as { content?: unknown } | undefined;
  if (body?.content !== undefined) return parseBodyContent(body.content);
  // H5 SDK 直存兜底：可能直接是 { text: "..." } 或 { zh_cn: {...} }
  return content;
}

export function ContentRenderer({ item }: { item: Item }) {
  const body = extractBody(item.content);

  switch (item.msg_type) {
    case "text":
      return <TextBlock text={String(body.text ?? item.content_text ?? "")} />;

    case "post":
      return <PostBlock body={body} fallback={item.content_text} />;

    case "image":
      return <Placeholder icon="🖼" label="图片消息" hint={item.content_text || undefined} />;

    case "file":
      return (
        <Placeholder
          icon="📎"
          label={String(body.file_name ?? "文件")}
          hint={item.content_text || undefined}
        />
      );

    case "sticker":
      return <Placeholder icon="😀" label="表情" />;

    case "audio":
      return <Placeholder icon="🎙" label="语音消息" />;

    case "video":
      return <Placeholder icon="🎬" label="视频" />;

    case "interactive": {
      const title = pickInteractiveTitle(body);
      return (
        <Placeholder
          icon="📋"
          label={title || "卡片消息"}
          hint={item.content_text || undefined}
        />
      );
    }

    default:
      return (
        <Placeholder
          icon="📄"
          label={`未识别类型：${item.msg_type}`}
          hint="此类型暂不支持渲染，可正常转发"
        />
      );
  }
}

function TextBlock({ text }: { text: string }) {
  // 简单的 URL 自动链接：只识别 http(s)://，避免误报。
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return (
    <div className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-800">
      {parts.map((p, i) =>
        /^https?:\/\//.test(p) ? (
          <a
            key={i}
            href={p}
            target="_blank"
            rel="noreferrer"
            className="text-sky-600 underline decoration-sky-300 hover:decoration-sky-600"
          >
            {p}
          </a>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </div>
  );
}

interface PostSegment {
  tag: string;
  text?: string;
  href?: string;
  user_name?: string;
  [key: string]: unknown;
}

function PostBlock({ body, fallback }: { body: Record<string, unknown>; fallback: string }) {
  // 飞书 post 结构：`{ zh_cn: { title, content: [[segments], ...] } }`
  const zh = (body.zh_cn ?? body.en_us ?? body.ja_jp) as
    | { title?: string; content?: PostSegment[][] }
    | undefined;

  if (!zh || !Array.isArray(zh.content)) {
    return <TextBlock text={fallback} />;
  }

  return (
    <div className="space-y-1 text-sm leading-relaxed text-slate-800">
      {zh.title ? <div className="font-semibold">{zh.title}</div> : null}
      {zh.content.map((para, i) => (
        <div key={i} className="whitespace-pre-wrap break-words">
          {para.map((seg, j) => {
            if (seg.tag === "a") {
              return (
                <a
                  key={j}
                  href={seg.href ?? "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sky-600 underline decoration-sky-300"
                >
                  {seg.text ?? seg.href}
                </a>
              );
            }
            if (seg.tag === "at") {
              return (
                <span key={j} className="text-sky-700">
                  @{seg.user_name ?? "某人"}
                </span>
              );
            }
            return <span key={j}>{String(seg.text ?? "")}</span>;
          })}
        </div>
      ))}
    </div>
  );
}

function Placeholder({
  icon,
  label,
  hint,
}: {
  icon: string;
  label: string;
  hint?: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded border border-dashed border-slate-200 bg-slate-50/60 px-3 py-2 text-sm text-slate-600">
      <span className="text-lg">{icon}</span>
      <div className="min-w-0">
        <div className="truncate font-medium text-slate-700">{label}</div>
        {hint ? <div className="truncate text-xs text-slate-500">{hint}</div> : null}
      </div>
    </div>
  );
}

function pickInteractiveTitle(body: Record<string, unknown>): string {
  const header = body.header as { title?: { content?: string } } | undefined;
  return header?.title?.content ?? "";
}
